import { Skeleton } from "@radix-ui/themes";
import { useEffect, useRef, useCallback } from "react";

import { useKnowledgeHub } from "../wrapper/useKnowledgeHub";
import { useDebouncedCallback } from "@/lib/useDebouncedCallback";

import styles from "./styles.module.scss";

export const AskKnowledge = ({
  configurationToken,
}: {
  configurationToken: string;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isWidgetInitialized = useRef(false);

  const { config, loadingConfig } = useKnowledgeHub();

  const UPDATE_DEBOUNCE_DELAY = 2000;
  const updateHubConfig = useCallback((token: string, newConfig: any) => {
    const hub = (window as any).KnowledgeHub;
    if (hub?.update) {
      hub.update({
        token,
        config: newConfig,
      });
    }
  }, []);

  const debouncedUpdate = useDebouncedCallback(
    updateHubConfig,
    UPDATE_DEBOUNCE_DELAY
  );

  useEffect(() => {
    const SCRIPT_ID = "knowledge-hub-script";

    const loadExternalScript = async () => {
      try {
        if (!document.getElementById(SCRIPT_ID)) {
          const script = document.createElement("script");
          script.id = SCRIPT_ID;
          script.src =
            "https://quack-stg-quack-knowledge-hub.s3.us-east-1.amazonaws.com/dist/knowledge-hub.js";
          script.async = true;
          document.body.appendChild(script);
        }
      } catch (error) {
        console.error("Failed to load Knowledge Hub script:", error);
      }
    };

    loadExternalScript();

    return () => {
      const script = document.getElementById(SCRIPT_ID);
      if (script) script.remove();
      if ((window as any).KnowledgeHub) {
        delete (window as any).KnowledgeHub;
      }
    };
  }, []);

  useEffect(() => {
    if (!configurationToken || !config || !containerRef.current) return;

    const getHub = () => (window as any).KnowledgeHub;

    let pollingInterval: NodeJS.Timeout;
    let pollingTimeout: NodeJS.Timeout;

    // if the widget is already running, update the config store.
    if (isWidgetInitialized.current && getHub()?.update) {
      debouncedUpdate(configurationToken, config);
    } else {
      // if the widget is not running, mount it for the first time.
      const tryInit = () => {
        if (getHub()?.init && containerRef.current) {
          containerRef.current.innerHTML = "";

          getHub().init({
            token: configurationToken,
            selector: "#my-hub-container",
            theme: config.theme?.mode || "system",
            config: config,
          });

          isWidgetInitialized.current = true;
          return true;
        }
        return false;
      };

      // If script isn't ready, poll for it
      if (!tryInit()) {
        // Poll if not ready
        pollingInterval = setInterval(() => {
          if (tryInit()) clearInterval(pollingInterval);
        }, 100);

        pollingTimeout = setTimeout(() => {
          clearInterval(pollingInterval);
        }, 5000);
      }
    }

    return () => {
      clearInterval(pollingInterval);
      clearTimeout(pollingTimeout);
    };
  }, [configurationToken, config, loadingConfig, debouncedUpdate]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        flex: "1 1 auto",
        height: "auto",
      }}
    >
      <Skeleton
        className={styles.title}
        loading={loadingConfig}
        style={{ flex: 1, display: "flex" }}
      >
        <div
          id="my-hub-container"
          ref={containerRef}
          style={{
            width: "100%",
            flex: "1 1 auto",
            height: "auto",
          }}
        />
      </Skeleton>
    </div>
  );
};
